/**
 * Correct what the scanner read, then send it to the guest.
 *
 * Two ways to fix a value, because receptionists use both: click into the
 * value on the page and type, as in a document, or work down the list on the
 * right.
 * They are the same edit — the list and the page stay in step, and either way
 * the correction is saved as it is made rather than held in the browser until
 * someone remembers to press a button.
 *
 * Every field the template defines has a row here, including the ones the
 * scanner never read (the tick-box-derived Mr/Mrs/Miss and payment-method
 * fields the legacy config.ini produced with no extract region). They are
 * stamped onto the finished PDF, so they have to be fillable.
 *
 * Two ways to leave, and they are not neighbours. "Close" puts the document
 * down: the pane empties, the card stays in the inbox, nothing is lost.
 * "Delete" removes the capture, and lives up in the header with the document's
 * other operations. They used to be the same button - the footer offered only
 * "Discard", so the receptionist who simply wanted the pane back pressed the
 * one control that destroyed the document.
 */

import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Loader2, RotateCcw, Send, Tags, Trash2, X } from 'lucide-react'
import { api } from '@/lib/api'
import type { CaptureJob, SessionField } from '@/lib/types'
import { Badge, Button, Card } from '@/components/ui/primitives'
import { useToast } from '@/components/ui/toast'
import {
  DocumentViewer,
  PageFitToggle,
  PageNavigator,
  usePageFit,
} from '@/components/domain/DocumentViewer'
import { ConfirmDestructive } from '@/components/domain/ConfirmDestructive'
import { DevicePicker } from '@/features/workspace/DevicePicker'
import { FieldDeck } from '@/features/workspace/FieldDeck'
import { useCorrections } from '@/features/workspace/useCorrections'
import { cn } from '@/lib/utils'

export function ExtractionReview({
  job,
  onPushed,
  onClose,
  onDeleted,
  onCorrected,
  onDirtyChange,
}: {
  job: CaptureJob
  onPushed: () => void
  /** Put the document down. The capture is untouched and stays in the inbox. */
  onClose: () => void
  onDeleted: () => void
  onCorrected: () => void
  onDirtyChange?: (dirty: boolean) => void
}) {
  const toast = useToast()
  const edits = useCorrections(job)
  const [page, setPage] = useState(1)
  const [activeField, setActiveField] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [typePickerOpen, setTypePickerOpen] = useState(false)
  const [deviceId, setDeviceId] = useState<string | null>(null)

  // `job.extractions ?? []` built a fresh array on every render, so every memo
  // downstream of it recomputed every render and memoised nothing. A capture
  // can carry thirty fields and this screen re-renders on each keystroke.
  const extractions = useMemo(() => job.extractions ?? [], [job.extractions])
  const groups = useMemo(() => job.checkbox_groups ?? [], [job.checkbox_groups])
  const value = edits.value

  const missingRequired = useMemo(
    () => extractions.filter((e) => e.is_required && !value(e).trim()).map((e) => e.label),
    [extractions, value],
  )

  const reviewCount = extractions.filter((e) => e.needs_review).length

  useEffect(() => onDirtyChange?.(edits.dirtyCount > 0), [edits.dirtyCount, onDirtyChange])

  // Clamp to a page the document actually has — a two-page capture followed by
  // a one-page one used to leave the navigator pointing at a page that was not
  // there.
  const pages = useMemo(
    () =>
      (job.pages ?? []).map((p) => ({
        page: p.page_number,
        image_url: p.image_url,
        width_px: p.width_px,
        height_px: p.height_px,
      })),
    [job.pages],
  )
  useEffect(() => {
    if (pages.length && !pages.some((p) => p.page === page)) setPage(pages[0].page)
  }, [pages, page])

  const fit = usePageFit(pages.find((p) => p.page === page) ?? pages[0])

  // The viewer wants session-shaped fields; the review screen has capture-shaped
  // ones. Mapping here keeps one viewer instead of two.
  const viewerFields: SessionField[] = useMemo(
    () =>
      extractions.map((extraction) => ({
        field_key: extraction.field_key,
        label: extraction.label,
        page: extraction.overlay?.page ?? extraction.page,
        data_type: extraction.data_type,
        guest_editable: extraction.guest_editable,
        required: extraction.is_required,
        value: value(extraction),
        confidence: extraction.confidence,
        needs_review: extraction.needs_review,
        max_length: extraction.max_length,
        placeholder: extraction.placeholder,
        help_text: extraction.help_text,
        options: extraction.options,
        overlay: extraction.overlay
          ? {
              x: extraction.overlay.x,
              y: extraction.overlay.y,
              w: extraction.overlay.w,
              h: extraction.overlay.h,
            }
          : null,
      })),
    [extractions, value],
  )

  function focusField(fieldKey: string, fromDocument = false) {
    setActiveField(fieldKey)
    const extraction = extractions.find((e) => e.field_key === fieldKey)
    if (extraction?.overlay) setPage(extraction.overlay.page)
    // Picked in the list: the page is wider than the screen is tall now, so
    // the value it points at may be scrolled out of sight.
    if (!fromDocument) {
      fit.reveal(fieldKey)
      return
    }
    // Clicking a box on the page has to move the list, or the only feedback is
    // a ring on a row that is scrolled out of sight.
    const input = document.getElementById(`field-${fieldKey}`)
    input?.scrollIntoView({ block: 'nearest' })
  }

  const push = useMutation({
    mutationFn: async () => {
      // Anything still settling goes now, so the tablet never receives a
      // document that is one keystroke behind what is on screen.
      await edits.flush()
      const session = await api.post<{ id: string }>('/sessions', {
        capture_job_id: job.id,
        device_id: deviceId,
      })
      await api.post(`/sessions/${session.id}/push`)
      return session
    },
    onSuccess: () => {
      toast.success('Sent to the tablet', 'The guest can review and sign now.')
      onPushed()
    },
    onError: (error: Error) => toast.error('Could not send the document', error.message),
  })

  const retry = useMutation({
    mutationFn: async () => {
      await edits.flush()
      return api.post(`/capture/jobs/${job.id}/retry`)
    },
    onSuccess: () => {
      toast.info('Reading the document again')
      onCorrected()
    },
    onError: (error: Error) => toast.error('Could not re-read the document', error.message),
  })

  const remove = useMutation({
    mutationFn: () => api.delete(`/capture/jobs/${job.id}`),
    onSuccess: () => {
      toast.success('Document deleted')
      onDeleted()
    },
    onError: (error: Error) => toast.error('Could not delete the document', error.message),
  })

  return (
    <Card className="overflow-hidden xl:flex xl:min-h-0 xl:flex-1 xl:flex-col">
      <header className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-[var(--border)] px-3 py-2">
        <div className="flex min-w-0 items-baseline gap-2">
          <h2 className="truncate text-[15px] font-semibold">
            {job.document_type_label ?? 'Unidentified document'}
          </h2>
          <p className="truncate text-[12px] text-[var(--text-muted)]">
            {job.page_count ?? 0} page{job.page_count === 1 ? '' : 's'}
            {job.detection_score != null &&
              ` · identified with ${Math.round(job.detection_score * 100)}% confidence`}
          </p>
        </div>
        {/* The page's own controls, up here rather than in a row under the
            page that took its height. */}
        <PageNavigator pages={pages} current={page} onChange={setPage} />
        <PageFitToggle mode={fit.mode} onChange={fit.setMode} />
        <div className="ms-auto flex items-center gap-1">
          <SaveState saving={edits.saving} failed={edits.failed.length} />
          {reviewCount > 0 && <Badge tone="warning">{reviewCount} to check</Badge>}
          {/* Labels from 2xl; an icon and its tooltip below that, so the header
              stays one line on a laptop. */}
          <Button
            variant="ghost"
            size="sm"
            className="px-2"
            title="Change type"
            aria-label="Change type"
            icon={<Tags className="h-3.5 w-3.5" />}
            onClick={() => setTypePickerOpen((open) => !open)}
          >
            <span className="hidden 2xl:inline">Change type</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="px-2"
            title="Read it again"
            aria-label="Re-read"
            icon={<RotateCcw className="h-3.5 w-3.5" />}
            loading={retry.isPending}
            onClick={() => retry.mutate()}
          >
            <span className="hidden 2xl:inline">Re-read</span>
          </Button>
          {/* Up here with "change its type" and "read it again", because it is
              the third thing you can do to this document - not, as it was, the
              button sitting where "I am finished with this" belongs. */}
          <Button
            variant="ghost"
            size="sm"
            className="px-2 text-[var(--danger)]"
            title="Delete this document"
            aria-label="Delete"
            icon={<Trash2 className="h-3.5 w-3.5" />}
            loading={remove.isPending}
            onClick={() => setConfirmDelete(true)}
          >
            <span className="hidden 2xl:inline">Delete</span>
          </Button>
        </div>
      </header>

      {typePickerOpen && (
        <TypeOverridePanel
          jobId={job.id}
          currentType={job.document_type}
          beforeChange={edits.flush}
          onDone={() => {
            setTypePickerOpen(false)
            onCorrected()
          }}
        />
      )}

      {/* At xl the card is as tall as the screen and the page is the widest
          thing in it. "Fit width" gives the page most of the card and scrolls
          it; "Whole page" makes its column exactly as wide as a page of the
          card's height, and everything else goes to the fields. */}
      <div
        className={cn(
          'grid gap-3 p-3 xl:min-h-0 xl:flex-1 xl:grid-rows-[minmax(0,1fr)]',
          fit.mode === 'page'
            ? 'xl:grid-cols-[minmax(0,var(--doc-w))_minmax(20rem,1fr)]'
            : 'xl:grid-cols-[minmax(0,5fr)_minmax(17rem,2fr)]',
        )}
        style={{ '--doc-w': fit.width ? `${fit.width}px` : '1fr' } as CSSProperties}
      >
        <div ref={fit.ref} className="scroll-thin min-w-0 xl:min-h-0 xl:overflow-y-auto">
          <DocumentViewer
            mode="review"
            pages={pages}
            fields={viewerFields}
            checkboxGroups={groups}
            currentPage={page}
            onFieldFocus={(key) => focusField(key, true)}
            onFieldChange={edits.setValue}
            onCheckboxToggle={(groupKey, optionKey, selected) => {
              const group = groups.find((g) => g.group_key === groupKey)
              if (!group) return
              const current = group.options.filter((o) => o.selected).map((o) => o.option_key)
              const next = !selected
                ? current.filter((key) => key !== optionKey)
                : group.multi_select
                  ? [...new Set([...current, optionKey])]
                  : [optionKey]
              edits.setCheckbox(groupKey, next)
            }}
          />
        </div>

        <div className="flex min-w-0 flex-col gap-3 xl:min-h-0">
          {/* A *flex* column, not a plain block. As a block this capped
              nothing: the deck's own height stayed its content height, so a
              thirty-field card ran 1000px past the bottom of this box, out
              through the card, and the separator and the footer - "Close",
              "Send to tablet" - drew straight across the last rows of fields.
              Capping a flex column makes the deck shrink to it and scroll
              inside it, which is what the 68vh was for. */}
          <div className="flex max-h-[68vh] min-h-0 flex-col xl:max-h-none xl:flex-1">
            <FieldDeck
              fields={extractions}
              groups={groups}
              edits={edits}
              activeField={activeField}
              onFocusField={focusField}
            />
          </div>

          {/* Under the fields rather than across the whole card: a full-width
              footer was a band of height taken from the page to hold three
              buttons. */}
          <footer className="flex shrink-0 flex-col gap-2 border-t border-[var(--border)] pt-3">
            {missingRequired.length > 0 && (
              <p className="text-[12px] text-[var(--warning)]">
                Still needed: {missingRequired.join(', ')}
              </p>
            )}
            <DevicePicker value={deviceId} onChange={setDeviceId} />
            <div className="flex items-center gap-2">
              {/* No confirmation: nothing is destroyed, and corrections are
                  already saved as they are typed - anything still inside its
                  debounce is flushed when this component unmounts. */}
              <Button
                variant="ghost"
                size="sm"
                icon={<X className="h-3.5 w-3.5" />}
                onClick={onClose}
              >
                Close
              </Button>
              <Button
                variant="primary"
                className="ms-auto"
                icon={<Send className="h-4 w-4" />}
                loading={push.isPending}
                disabled={missingRequired.length > 0 || !deviceId}
                onClick={() => push.mutate()}
              >
                Send to tablet
              </Button>
            </div>
          </footer>
        </div>
      </div>

      <ConfirmDestructive
        open={confirmDelete}
        title="Delete this document?"
        description="It leaves the inbox and the captured PDF is removed, so it cannot be sent to a guest. If you only want the pane back, close it instead — the document stays where it is. If this one came from the PMS you can simply print it again."
        confirmLabel="Delete"
        loading={remove.isPending}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => {
          setConfirmDelete(false)
          remove.mutate()
        }}
      />
    </Card>
  )
}

function SaveState({ saving, failed }: { saving: boolean; failed: number }) {
  if (failed > 0) {
    return <Badge tone="danger">{failed} not saved</Badge>
  }
  return (
    <span
      className={cn(
        'flex items-center gap-1.5 text-[12px] text-[var(--text-muted)] transition-opacity',
        saving ? 'opacity-100' : 'opacity-0',
      )}
      role="status"
      aria-live="polite"
    >
      <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
      Saving…
    </span>
  )
}

function TypeOverridePanel({
  jobId,
  currentType,
  beforeChange,
  onDone,
}: {
  jobId: string
  currentType: string | null
  beforeChange: () => Promise<void>
  onDone: () => void
}) {
  const toast = useToast()
  const types = useQuery({
    queryKey: ['document-types'],
    queryFn: () => api.get<{ id: string; key: string; name: string }[]>('/document-types'),
  })

  const override = useMutation({
    mutationFn: async (documentTypeId: string) => {
      await beforeChange()
      return api.post(`/capture/jobs/${jobId}/override-type`, {
        document_type_id: documentTypeId,
        re_extract: true,
      })
    },
    onSuccess: () => {
      toast.success('Document type changed', 'Re-reading with the new template.')
      onDone()
    },
    onError: (error: Error) => toast.error('Could not change the type', error.message),
  })

  return (
    <div className="shrink-0 border-b border-[var(--border)] bg-[var(--surface-2)] px-4 py-3">
      <p className="mb-2 text-[12px] text-[var(--text-muted)]">
        Pick the correct type. The document is read again with that template.
      </p>
      <div className="flex flex-wrap gap-2">
        {(types.data ?? []).map((type) => (
          <Button
            key={type.id}
            size="sm"
            variant={type.key === currentType ? 'subtle' : 'secondary'}
            loading={override.isPending && override.variables === type.id}
            onClick={() => override.mutate(type.id)}
          >
            {type.name}
          </Button>
        ))}
      </div>
    </div>
  )
}
